'use client'

import Link from 'next/link'
import { DM_Mono, DM_Sans } from 'next/font/google'
import { Blocks, Bot, ClipboardCheck, GitBranch, Sparkles, UsersRound, Workflow } from 'lucide-react'
import { trackPostHogEvent } from '@/lib/utils/dataLayer'
import PriorityServicesBadge from '@/components/PriorityServicesBadge'
import { SERVICE_CATEGORY_TABS, type ServiceCategoryVariant } from '@/lib/service-category-tabs'
import { SMART_SERVICES_CRM } from '@/lib/smart-services-data'
import { SMART_AUDITS_HERO, SMART_AUDITS_INCLUDED } from '@/lib/smart-audits-data'
import { SMART_AI_HERO, SMART_AI_WAVE } from '@/lib/smart-ai-data'

const dmSans = DM_Sans({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
})

const dmMono = DM_Mono({
  subsets: ['latin'],
  weight: ['400', '500'],
  variable: '--svc-partner-mono',
})

const STANDARD_CHECKS = [
  'Work directly with me, quote in a day',
  'Written documentation & video walkthrough',
  'Your private project management portal',
  'White-label available',
]

const STANDARD_PRODUCTS = SERVICE_CATEGORY_TABS.map((tab) => ({
  name: tab.label,
  tip: tab.tip,
  variant: tab.variant,
  icon: <tab.Icon strokeWidth={1.75} />,
}))

const PRIORITY_CHECKS = [
  'CRM built and maintained',
  'Stack cost optimization',
  'Tools integrated',
  'Every lead traced to revenue',
  'Workflows that run themselves',
]

const CRM_LOGOS = [
  { name: 'HubSpot', src: '/images/logo-hubspot.svg' },
  { name: 'Zoho CRM', src: '/images/logo-zoho.png' },
  { name: 'Salesforce', src: '/images/logo-salesforce.svg' },
  { name: 'Pipedrive', src: '/images/logo-pipedrive.png' },
  { name: 'HighLevel', src: '/images/logo-highlevel.png' },
  { name: 'Keap', src: '/images/logo-keap.png' },
  { name: 'Workiz', src: '/images/logo-workiz.png' },
  { name: 'Housecall Pro', src: '/images/logo-housecallpro.png' },
  { name: 'ServiceTitan', src: '/images/logo-servicetitan.png' },
  { name: 'JobTread', src: '/images/logo-jobtread.png' },
]

const PRIORITY_PRODUCTS = [
  {
    name: 'AI',
    tip: 'Claude, ChatGPT and Gemini wired into your systems. They take actions, not just answer questions.',
    variant: 'violet' as const,
    icon: <Sparkles strokeWidth={1.75} />,
  },
  {
    name: 'CRM',
    tip: 'Built, configured, and connected to everything else: Zoho, HubSpot, or what you already run.',
    variant: 'blue' as const,
    icon: <UsersRound strokeWidth={1.75} />,
  },
  {
    name: 'Integrations',
    tip: 'Your tools talking to each other, and kept talking as they change.',
    variant: 'teal' as const,
    icon: <Blocks strokeWidth={1.75} />,
  },
  {
    name: 'Attribution',
    tip: 'Every lead traced to source, so you know what earned the revenue, not just what spent.',
    variant: 'amber' as const,
    icon: <GitBranch strokeWidth={1.75} />,
  },
  {
    name: 'Automation',
    tip: 'Follow-up, routing and internal workflows that run without anyone touching them.',
    variant: 'emerald' as const,
    icon: <Workflow strokeWidth={1.75} />,
  },
]

type FeatureItem = {
  name: string
  tip: string
  variant: ServiceCategoryVariant | 'green' | 'blue' | 'purple' | 'pink'
  icon: React.ReactNode
}

function FeatureList({ items, className }: { items: readonly FeatureItem[]; className?: string }) {
  return (
    <div className={className}>
      {items.map((item) => (
        <div key={item.name} className="svc-product">
          <span className="svc-product-name">{item.name}</span>
          <span className="svc-tip">
            <button type="button" className="svc-tip-btn" aria-label={`${item.name} details`}>
              i
            </button>
            <span className="svc-tip-pop" role="tooltip">
              {item.tip}
            </span>
          </span>
        </div>
      ))}
    </div>
  )
}

function track(text: string) {
  trackPostHogEvent('cta_click', {
    cta_text: text,
    cta_location: 'homepage_service_tiers',
    cta_type: 'primary',
  })
}

function Pill({ href, children }: { href: string; children: string }) {
  const isHash = href.startsWith('#')
  return (
    <Link
      href={href}
      className="svc-pill"
      onClick={(e) => {
        track(children)
        if (!isHash) return
        e.preventDefault()
        document.getElementById(href.slice(1))?.scrollIntoView({ behavior: 'smooth' })
      }}
    >
      {children}
    </Link>
  )
}

export function ServiceTierCards({
  systemsFirst = false,
  systemsCtaHref = '/services',
  systemsCtaLabel = 'Get Started',
  systemsTitle = 'Smart Ops',
  marketingCtaHref = '/services',
  marketingCtaLabel = 'View Services',
  hideProductSlugs = [],
  hideCheckItems = [],
  linkCatalog = true,
}: {
  systemsFirst?: boolean
  systemsCtaHref?: string
  systemsCtaLabel?: string
  systemsTitle?: string
  marketingCtaHref?: string
  marketingCtaLabel?: string
  hideProductSlugs?: string[]
  hideCheckItems?: string[]
  linkCatalog?: boolean
}) {
  const marketingProducts = hideProductSlugs.length
    ? STANDARD_PRODUCTS.filter((_, i) => !hideProductSlugs.includes(SERVICE_CATEGORY_TABS[i].slug))
    : STANDARD_PRODUCTS
  const marketingChecks = hideCheckItems.length
    ? STANDARD_CHECKS.filter((item) => !hideCheckItems.includes(item))
    : STANDARD_CHECKS
  const systemsChecks = PRIORITY_CHECKS.slice(1).filter((item) => !hideCheckItems.includes(item))

  const marketing = (
    <article className="svc-card">
      <div className="svc-card-body">
        <div className="svc-card-heading">
          <h3>Marketing Services</h3>
        </div>
        <p className="svc-card-copy">
          Book what you need - a single project or ongoing work. Quoted before anything starts.
        </p>
        <FeatureList items={marketingProducts} className="svc-card-products" />
        <ul className="svc-checks">
          <li>
            Full catalog -{' '}
            {linkCatalog ? (
              <Link href="/services" className="svc-check-link" onClick={() => track('100+ services')}>
                100+ services
              </Link>
            ) : (
              '100+ services'
            )}{' '}
            to pick from
          </li>
          <li>
            Free{' '}
            <Link
              href="/resources/free-seo-checklist"
              className="svc-check-link"
              onClick={() => track('SEO checklist')}
            >
              Local SEO checklist
            </Link>
          </li>
          {marketingChecks.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>

      <div className="svc-card-footer">
        <Pill href={marketingCtaHref}>{marketingCtaLabel}</Pill>
      </div>
    </article>
  )

  const systems = (
    <article className="svc-card svc-card--priority">
      <div className="svc-card-body">
        <div className="svc-card-heading">
          <h3>{systemsTitle}</h3>
          <PriorityServicesBadge size="sm" />
        </div>
        <p className="svc-card-copy">
          The systems your business runs on - built, connected, and kept working
        </p>
        <FeatureList items={PRIORITY_PRODUCTS} className="svc-card-products" />
        <ul className="svc-checks">
          <li className="svc-check-with-logos">
            <span>CRM built and maintained</span>
            <span className="svc-brand-logos svc-brand-logos--wrap">
              {CRM_LOGOS.map((logo) => (
                // eslint-disable-next-line @next/next/no-img-element
                <img key={logo.name} src={logo.src} alt={logo.name} title={logo.name} width={20} height={20} />
              ))}
            </span>
          </li>
          {systemsChecks.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
      <div className="svc-card-footer">
        <Pill href={systemsCtaHref}>{systemsCtaLabel}</Pill>
      </div>
    </article>
  )

  return (
    <div className={`svc-pricing-grid ${dmSans.className}`}>
      {systemsFirst ? (
        <>
          {systems}
          {marketing}
        </>
      ) : (
        <>
          {marketing}
          {systems}
        </>
      )}
    </div>
  )
}

function HomepageOfferCards() {
  return (
    <div className={`svc-pricing-grid svc-pricing-grid--3 ${dmSans.className}`}>
      <article className="svc-card">
        <div className="svc-card-body">
          <div className="svc-card-heading">
            <div className="svc-card-titleblock">
              <span className="svc-card-icon" aria-hidden>
                <ClipboardCheck size={30} strokeWidth={1.7} />
              </span>
              <div>
                <h3>Audit</h3>
                <p className="svc-card-sub">Fixed-price</p>
              </div>
            </div>
          </div>
          <p className="svc-card-copy">{SMART_AUDITS_HERO.sub}</p>
          <ul className="svc-checks">
            {SMART_AUDITS_INCLUDED.getItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li>
              {SMART_AUDITS_INCLUDED.creditLabel}: {SMART_AUDITS_INCLUDED.credit}
            </li>
          </ul>
        </div>
        <div className="svc-card-footer">
          <Pill href="/audit">See all audits</Pill>
        </div>
      </article>

      <article className="svc-card svc-card--priority">
        <div className="svc-card-body">
          <div className="svc-card-heading">
            <div className="svc-card-titleblock">
              <span className="svc-card-icon" aria-hidden>
                <Blocks size={30} strokeWidth={1.7} />
              </span>
              <div>
                <h3>Build</h3>
                <p className="svc-card-sub">Marketing & CRM</p>
              </div>
            </div>
          </div>
          <p className="svc-card-copy">{SMART_SERVICES_CRM.lead}</p>
          <ul className="svc-checks">
            <li>
              <span>
                <strong>Marketing ops:</strong> Ads, SEO, CRO, analytics
              </span>
            </li>
            <li className="svc-check-with-logos">
              <span>
                <strong>Sales ops:</strong> CRM built and maintained
              </span>
              <span className="svc-brand-logos svc-brand-logos--wrap">
                {CRM_LOGOS.map((logo) => (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img key={logo.name} src={logo.src} alt={logo.name} title={logo.name} width={20} height={20} />
                ))}
              </span>
            </li>
            <li>
              <span>
                <strong>Data ops:</strong> tracking, attribution, consent
              </span>
            </li>
            {STANDARD_CHECKS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
        <div className="svc-card-footer">
          <Pill href="/services">See Build services</Pill>
        </div>
      </article>

      <article className="svc-card">
        <div className="svc-card-body">
          <div className="svc-card-heading">
            <div className="svc-card-titleblock">
              <span className="svc-card-icon" aria-hidden>
                <Bot size={30} strokeWidth={1.7} />
              </span>
              <div>
                <h3>Automate</h3>
                <p className="svc-card-sub">AI agents & flows</p>
              </div>
            </div>
          </div>
          <p className="svc-card-copy">
            {SMART_AI_WAVE.lead} {SMART_AI_WAVE.leadEm}
          </p>
          <ul className="svc-checks">
            <li>Every lead answered in seconds</li>
            <li>Follow-ups that run until the lead converts</li>
            <li>More closed deals from the leads you already pay for</li>
            <li className="svc-check-with-logos">
              <span>Runs on Claude, ChatGPT, or Gemini</span>
              <span className="svc-brand-logos">
                {SMART_AI_HERO.models.map((logo) => (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img key={logo.name} src={logo.src} alt={logo.name} width={20} height={20} />
                ))}
              </span>
            </li>
            <li>Plugs into your CRM, phone, and site</li>
            <li>Live in days, ongoing maintenance included</li>
          </ul>
        </div>
        <div className="svc-card-footer">
          <Pill href="/ai">See AI services</Pill>
        </div>
      </article>
    </div>
  )
}

export default function ServiceTiers() {
  return (
    <section id="services" className={`svc-pricing ${dmSans.className}`} aria-label="Audit, Build, and Automate">
      <div className="svc-pricing-title">
        <h2 className="svc-pricing-headline">
          <span className="svc-pricing-mark">Grow</span> at your pace
        </h2>
        <p className="svc-pricing-lede">
          Start with a fixed-price audit, book the work, or put AI in the stack.
        </p>
      </div>

      <HomepageOfferCards />

      <div className={`svc-partner ${dmMono.variable}`}>
        <div className="svc-partner-inner">
          <div className="svc-partner-top">
            <div className="svc-partner-lead">
              <p className="svc-partner-eyebrow">Is this you?</p>
              <p className="svc-partner-who">
                If your systems <b>don&apos;t talk to each other</b>, you can&apos;t see what&apos;s actually making money.
              </p>
            </div>
            <div className="svc-partner-media">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/images/crm.png" alt="Salesforce, HubSpot, Zoho, ActiveCampaign, ClickUp, Pipedrive and HighLevel connected through SmartMetrics" />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
